import AIIntegrationApi from "@api/aiIntegration.api";
import { ElNotification } from "element-plus";

/**
 * AI Form Processor Service
 * Handles AI processing for document uploads across all category components
 */
class AIFormProcessor {
    constructor() {
        this.isProcessing = false;
    }

    /**
     * Process AI analysis after file upload
     * @param {number} fileId - The uploaded file ID
     * @param {Object} document - The document object to populate
     * @returns {Promise<void>}
     */
    async processAIAnalysis(fileId, document) {
        try {
            this.isProcessing = true; // Start processing - disable form
            
            // Call AI integration API
            const aiResponse = await AIIntegrationApi.initDocumentsData({ file_id: fileId });
            
            if (aiResponse.data?.data) {
                const aiData = aiResponse.data.data;
                
                // Populate form fields with AI data
                this.populateFormFields(aiData, document);
                
                // Show success notification
                ElNotification({
                    message: "AI analysis completed! Form fields have been populated.",
                    type: "success",
                    position: "bottom-left",
                    duration: 5000,
                });
            }
            
        } catch (error) {
            console.error('AI processing failed:', error);
            
            ElNotification({
                message: "AI analysis failed. You can manually fill the form fields.",
                type: "warning",
                position: "bottom-left",
                duration: 5000,
            });
        } finally {
            this.isProcessing = false; // Done processing - enable form
        }
    }

    /**
     * Populate form fields with AI response data
     * @param {Object} aiData - AI response data
     * @param {Object} document - Document object to update
     */
    populateFormFields(aiData, document) {
        // Map the 5 required fields
        if (aiData.document_summary_title) {
            document.title = aiData.document_summary_title;
        }
        if (aiData.document_context_background) {
            document.document_context = aiData.document_context_background;
        }
        if (aiData.document_purpose) {
            document.document_purpose = aiData.document_purpose;
        }
        if (aiData.authors && aiData.authors.length > 0) {
            // Map authors to user IDs
            const authorIds = aiData.authors
                .filter(author => author.user_id)
                .map(author => author.user_id);
            document.authors_ids = authorIds;
        }
        if (aiData.tags && aiData.tags.length > 0) {
            // Map tags to tag IDs
            const tagIds = aiData.tags.map(tag => tag.tag_id);
            document.tags = tagIds;
        }
    }

    /**
     * Process AI analysis for client URL
     * @param {string} clientUrl - The client website URL
     * @param {Object} client - The client object to populate
     * @returns {Promise<void>}
     */
    async processClientAIAnalysis(clientUrl, client) {
        try {
            this.isProcessing = true; // Start processing - disable form
            
            // Call AI integration API
            const aiResponse = await AIIntegrationApi.initClientData({ client_url: clientUrl });
            
            if (aiResponse.data?.data) {
                const aiData = aiResponse.data.data;
                
                // Populate form fields with AI data
                this.populateClientFormFields(aiData, client);
                
                // Show success notification
                ElNotification({
                    message: "AI analysis completed! Client fields have been populated.",
                    type: "success",
                    position: "bottom-left",
                    duration: 5000,
                });
            }
            
        } catch (error) {
            console.error('AI client processing failed:', error);
            
            ElNotification({
                message: "AI analysis failed. You can manually fill the form fields.",
                type: "warning",
                position: "bottom-left",
                duration: 5000,
            });
        } finally {
            this.isProcessing = false; // Done processing - enable form
        }
    }

    /**
     * Populate client form fields with AI response data
     * @param {Object} aiData - AI response data
     * @param {Object} client - Client object to update
     */
    populateClientFormFields(aiData, client) {
        // Map the client fields
        if (aiData.name) {
            client.name = aiData.name;
        }
        if (aiData.abbreviation) {
            client.abbreviation = aiData.abbreviation;
        }
        if (aiData.camp) {
            client.camp = aiData.camp;
        }
        if (aiData.client_type_id) {
            client.client_type_id = aiData.client_type_id;
        }
        if (aiData.industry_id) {
            client.industry_id = aiData.industry_id;
        }
    }

    /**
     * Get current processing state
     * @returns {boolean}
     */
    isAIProcessing() {
        return this.isProcessing;
    }
}

// Create singleton instance
const aiFormProcessor = new AIFormProcessor();

export default aiFormProcessor;
